import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import type {
  AddSopGuideUploadInput,
  BindSopGuideFrameInput,
  RemoveSopGuideFrameInput,
  ReorderSopGuideFramesInput,
  RefreshSopGuideFromRunInput,
  SaveSopGuideAnnotationsInput,
  SetSopGuideModeInput,
} from '@erp/rpc';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';

/** One SOP version's visual guide (frames + step bindings + mode). `version` omitted → published. */
export function useSopGuide(
  organizationId: string | undefined,
  sopId: string | undefined,
  version?: number,
) {
  return useQuery({
    ...orpc.agent.listSopGuide.queryOptions({
      input:
        organizationId && sopId
          ? { organizationId, sopId, version }
          : skipToken,
    }),
    enabled: Boolean(organizationId && sopId),
  });
}

export function useInvalidateSopGuide() {
  return useInvalidateRpcQueryFn((o) => [o.agent.listSopGuide.key()]);
}

/** Set a draft version's guide mode (off / on_heal / always). */
export function useSetSopGuideMode() {
  const invalidate = useInvalidateSopGuide();
  const { showToast } = useToast();
  const gt = useGT();
  return useMutation({
    mutationFn: (input: SetSopGuideModeInput) =>
      rpcClient.agent.setSopGuideMode(input),
    onSuccess: () => invalidate({}),
    onError: (error: Error) =>
      showToast({
        title: gt('Error', {
          context: 'toast title when updating the SOP visual guide mode fails',
        }),
        message:
          error.message ||
          gt('Failed to update the visual guide mode', {
            context:
              'toast message when updating the SOP visual guide mode fails',
          }),
        color: 'error',
      }),
  });
}

function useGuideMutation<TInput>(
  run: (input: TInput) => Promise<unknown>,
  failureMessage: string,
) {
  const invalidate = useInvalidateSopGuide();
  const { showToast } = useToast();
  const gt = useGT();
  return useMutation({
    mutationFn: run,
    onSuccess: () => invalidate({}),
    onError: (error: Error) =>
      showToast({
        title: gt('Error', {
          context: 'toast title when an SOP visual guide edit fails',
        }),
        message: error.message || failureMessage,
        color: 'error',
      }),
  });
}

export function useSaveSopGuideAnnotations() {
  const gt = useGT();
  return useGuideMutation(
    (input: SaveSopGuideAnnotationsInput) =>
      rpcClient.agent.saveSopGuideAnnotations(input),
    gt('Failed to save the marks', {
      context: 'toast message when saving SOP guide marks fails',
    }),
  );
}

export function useBindSopGuideFrame() {
  const gt = useGT();
  return useGuideMutation(
    (input: BindSopGuideFrameInput) => rpcClient.agent.bindSopGuideFrame(input),
    gt("Failed to update the frame's steps", {
      context: 'toast message when updating SOP guide step bindings fails',
    }),
  );
}

export function useReorderSopGuideFrames() {
  const gt = useGT();
  return useGuideMutation(
    (input: ReorderSopGuideFramesInput) =>
      rpcClient.agent.reorderSopGuideFrames(input),
    gt('Failed to reorder the frames', {
      context: 'toast message when reordering SOP guide frames fails',
    }),
  );
}

export function useRemoveSopGuideFrame() {
  const gt = useGT();
  return useGuideMutation(
    (input: RemoveSopGuideFrameInput) =>
      rpcClient.agent.removeSopGuideFrame(input),
    gt('Failed to remove the frame', {
      context: 'toast message when removing an SOP guide frame fails',
    }),
  );
}

export function useAddSopGuideUpload() {
  const gt = useGT();
  return useGuideMutation(
    (input: AddSopGuideUploadInput) => rpcClient.agent.addSopGuideUpload(input),
    gt('Failed to add the screenshot', {
      context: 'toast message when uploading an SOP guide screenshot fails',
    }),
  );
}

export function useRefreshSopGuideFromRun() {
  const gt = useGT();
  return useGuideMutation(
    (input: RefreshSopGuideFromRunInput) =>
      rpcClient.agent.refreshSopGuideFromRun(input),
    gt('Failed to refresh the frames from the last run', {
      context: 'toast message when refreshing SOP guide frames fails',
    }),
  );
}

/** Perceptual drift of each frame vs the latest completed replay. Fetched on demand. */
export function useSopGuideDrift(
  organizationId: string | undefined,
  sopId: string | undefined,
  version: number | undefined,
  enabled: boolean,
) {
  return useQuery({
    ...orpc.agent.sopGuideDrift.queryOptions({
      input:
        organizationId && sopId
          ? { organizationId, sopId, version }
          : skipToken,
    }),
    enabled: Boolean(organizationId && sopId) && enabled,
    staleTime: 60_000,
  });
}
